import React from "react";
import { Box, Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFetchTable } from "@hooks/useFetchDataTable";
import BackgroundImage from "@components/BackgroundImageCst";
import Table from "@components/Table";
import HeroCst from "@components/HeroCst";
import Breadcrumb from "@components/Breadcrumb";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useFetchDetail } from "@hooks/useFetchDetail";
import { ServicesURL } from "@utils/utilsApi";
import { LOType, PagesID } from "@utils/utilsConfigurations";
import { ActionsType } from "src/interfaces/Common";
import LeakAddIcon from "@mui/icons-material/LeakAdd";
import ButtonBackPage from "@components/ButtonBackPage";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";

const AssociationGroups: React.FC<{ type: LOType }> = ({ type }) => {
  const { id } = useParams();
  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${"association-groups" + type}`;
  const navigate = useNavigate();

  const { columns, dataTable } = useFetchTable(
    `${ServicesURL.groups.replace(":idCorporate", `${id ? id : 1}`)}/groups`,
    PagesID["corporate.groups"],
    filters[filterId],
  );
  const { t } = useTranslation(["groups-page", "modal"]);

  const { dataDetail } = useFetchDetail(
    ServicesURL.corporate,
    id || "",
    PagesID.corporate,
  );
  const location = useLocation();
  const actionsRow: ActionsType[] = React.useMemo(() => {
    const customActions = [
      {
        type: "button",
        icon: "lockUnlock",
        permission: "button",
        url: "enrollment",
        labelTranslate: t("association-groups.enroll-users"),
      },
    ];
    const enrollPrefix = "group-association";
    return customActions?.map((action: any) => {
      switch (action.type) {
        case "button": {
          return {
            ...action,
            key: "enroll",
            idPrefix: enrollPrefix,
            testId: `${enrollPrefix}-enroll-button`,
            rowIdGetter: (row: any) => row?.id,
            ariaLabel: action.labelTranslate,
            callBack: (row: any) => {
              navigate(`${row.type}/${row.id}/enrollment`);
            },
            getIcon: (row: any) => {
              return <LeakAddIcon />;
            },
          };
        }
        default:
          return action;
      }
    });
  }, [columns]);

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-courses.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={`${location.state?.row?.title || dataDetail?.business_name} - ${t(
            "association-groups.hero-title",
          )}`}
          description={t("association-groups.hero-description")}
        />
        <ButtonBackPage />
        {columns && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <Table
              id={"association-groups" + type}
              data={dataTable?.output}
              columns={columns || []}
              configurationTopToolbar={[]}
              enableRowActions={true}
              configurationActionForRow={actionsRow || []}
              totalPages={dataTable?.metadata?.total_elements}
              testIdPrefix="group-association"
            />
          </Box>
        )}
      </Container>
    </AnimationFadeIn>
  );
};

export default AssociationGroups;
